import React, { useEffect, useState } from 'react'
import {
  IconSquarePlus,
  IconChevronDown,
  IconChevronRight,
  IconDeviceDesktop
} from '@tabler/icons-react'
import Skeleton from 'react-loading-skeleton'
import { useWorkspaceService } from '../../../_services/WorkspaceService'
import { useWorkspaceLauncher } from './useWorkspaceLauncher'
import './styles.css'

interface ServerWorkspace {
  id: string | number
  name: string
  icon?: string
  [key: string]: any
}

interface WorkspaceSwitcherFlyoutProps {
  /** Whether the flyout is open (drives the workspace fetch). */
  isOpen: boolean
  /** Close the flyout (e.g. after launching a workspace). */
  onClose: () => void
  /** Open the full Create-workspace wizard. */
  onCreateNew: () => void
  /** Attached to the outer container so useComponentVisible in the parent can close on outside click. */
  ref?: React.Ref<HTMLDivElement>
}

/**
 * Tab-strip "+" flyout that mirrors the left-nav workspace submenu: it lists all
 * available workspaces (personal + team) grouped under headers, with a scrollable
 * body, and a "Create New" action that hands off to the Create-workspace wizard.
 *
 * The forwarded ref is attached to the outer container so `useComponentVisible`
 * in the parent can close the flyout on an outside click.
 */
const WorkspaceSwitcherFlyout = ({
  isOpen,
  onClose,
  onCreateNew,
  ref
}: WorkspaceSwitcherFlyoutProps) => {
  const { getWorkspaces } = useWorkspaceService()
  const { launchWorkspace } = useWorkspaceLauncher()
  const [personal, setPersonal] = useState<ServerWorkspace[]>([])
  const [team, setTeam] = useState<ServerWorkspace[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [personalOpen, setPersonalOpen] = useState(true)
  const [teamOpen, setTeamOpen] = useState(true)

  // Fetch the full workspace list from the server each time the flyout opens so
  // it reflects workspaces the user hasn't opened as tabs yet.
  useEffect(() => {
    if (!isOpen) return
    let cancelled = false
    const load = async () => {
      setIsLoading(true)
      try {
        const [personalResp, teamResp] = await Promise.all([
          getWorkspaces('Personal'),
          getWorkspaces('Team')
        ])
        if (cancelled) return
        // `smartflow` workspaces are an internal artefact, not user workspaces.
        setPersonal(
          (Array.isArray(personalResp) ? personalResp : []).filter(
            (ws: ServerWorkspace) => ws?.icon !== 'smartflow'
          )
        )
        setTeam(Array.isArray(teamResp) ? teamResp : [])
      } finally {
        if (!cancelled) setIsLoading(false)
      }
    }
    load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen])

  if (!isOpen) return null

  const handleLaunch = (ws: ServerWorkspace, privacy: 'personal' | 'team') => {
    launchWorkspace({ id: ws.id, name: ws.name }, privacy)
    onClose()
  }

  const renderItems = (items: ServerWorkspace[], privacy: 'personal' | 'team') =>
    items.map((ws) => (
      <button
        key={String(ws.id)}
        type="button"
        data-testid="workspace-switcher-item"
        onClick={() => handleLaunch(ws, privacy)}
        className="ws-switcher-item flex w-full items-center gap-[8px] rounded bg-transparent px-[8px] py-[6px] text-left text-sm text-[var(--text-body)]">
        <IconDeviceDesktop size={16} color="var(--icon-muted)" />
        <span className="truncate">{ws.name}</span>
      </button>
    ))

  const sectionHeader = (label: string, isOpenSection: boolean, onToggle: () => void) => (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={isOpenSection}
      className="ws-switcher-item flex w-full items-center justify-between rounded bg-transparent px-[8px] pb-[4px] pt-[8px] text-left text-[11px] font-semibold uppercase tracking-wide text-[var(--text-subheading)]">
      <span>{label}</span>
      {isOpenSection ? (
        <IconChevronDown size={14} color="var(--icon-muted)" />
      ) : (
        <IconChevronRight size={14} color="var(--icon-muted)" />
      )}
    </button>
  )

  // Skeleton rows that mirror the real item layout (icon + name) so the loading
  // state doesn't visually jump when the workspaces resolve.
  const renderSkeletonRows = (count: number) =>
    Array.from({ length: count }).map((_, i) => (
      <div
        key={i}
        className="flex w-full items-center gap-[8px] px-[8px] py-[6px]"
        aria-hidden="true">
        <Skeleton width={16} height={16} borderRadius={4} />
        <span className="flex-1">
          {/* eslint-disable-next-line no-mixed-operators */}
          <Skeleton width={`${70 - i * 12}%`} height={12} borderRadius={4} />
        </span>
      </div>
    ))

  return (
    <div
      ref={ref}
      data-testid="workspace-switcher-flyout"
      className="absolute left-0 top-[38px] z-[30] w-[240px] rounded-[4px] border border-[var(--border-strong)] bg-[var(--surface-bg)] shadow-[0px_2px_8px_rgba(0,0,0,0.15)]">
      <div className="max-h-[320px] overflow-y-auto p-[6px]">
        {sectionHeader('My workspaces', personalOpen, () => setPersonalOpen((v) => !v))}
        {personalOpen &&
          (isLoading ? (
            renderSkeletonRows(3)
          ) : personal.length ? (
            renderItems(personal, 'personal')
          ) : (
            <div className="px-[8px] py-[6px] text-sm text-[var(--text-subheading)]">
              No workspaces yet
            </div>
          ))}

        {!isLoading && team.length > 0 && (
          <>
            {sectionHeader('Team workspaces', teamOpen, () => setTeamOpen((v) => !v))}
            {teamOpen && renderItems(team, 'team')}
          </>
        )}
      </div>

      <div className="border-t border-[var(--frame-divider)] p-[6px]">
        <button
          type="button"
          data-testid="create-new-workspace"
          onClick={onCreateNew}
          className="ws-switcher-item flex w-full items-center gap-[8px] rounded bg-transparent px-[8px] py-[6px] text-left text-sm font-semibold text-[var(--color-primary)]">
          <IconSquarePlus size={18} color="var(--color-primary)" />
          <span>Create New</span>
        </button>
      </div>
    </div>
  )
}

WorkspaceSwitcherFlyout.displayName = 'WorkspaceSwitcherFlyout'

export default WorkspaceSwitcherFlyout
